import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { DisposableSafelly } from "@toro/common/shared/components/disposable-safelly/disposable-safelly.component";
import { OrderMarketTypeEnum } from "@toro/common/shared/enum/orders/market-type.enum";
import { OrderSideEnum } from "@toro/common/shared/enum/orders/side.enum";
import { StockCheckoutType } from "@toro/common/shared/models/stocks-exchange/stock-checkout.model";
import { SettingsService } from "@toro/common/shared/services/settings/settings.service";
import { filter } from "rxjs/operators";
import { ICheckoutStock } from "../../../../../interfaces/checkout.interface";

import { MatDialogRef } from "@angular/material";
import { Store } from "@ngrx/store";
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { AppState } from "@toro/common/shared/interfaces/app-state.interface";
import { ConsolidatedPosition } from "@toro/common/shared/models/pos-risk/consolidated-position";
import { FinancePosition } from "@toro/common/shared/models/pos-risk/finance-position";
import { DialogsService } from "@toro/common/shared/services/dialogs.service";
import { CheckoutService } from "../../../../../services/checkout.service";
import { TrackingsService } from '../../../../../services/tracking.service';
import { CheckoutNotifierActionType, CheckoutNotifierProductType, CheckoutNotifierService } from "../../../../services/checkout-notifier/checkout-notifier.service";
import { CheckoutGuaranteeWarningSidePanelComponent } from "../../../checkout-guarantee-warning-aside-panel/checkout-guarantee-warning-aside-panel.component";
import { calculateInvestment } from "../../utils/investment-calculation";
import { CheckoutAboutFeesComponent } from "../checkout-about-fees/checkout-about-fees.component";
import { CheckoutResumeComponent } from "../checkout-resume/checkout-resume.component";

@Component({
  selector: "checkout-investment",
  templateUrl: "./checkout-investment.component.html",
  styleUrls: ["./checkout-investment.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CheckoutInvestmentComponent extends DisposableSafelly implements OnInit {
  @Output() public close = new EventEmitter();
  public stockId: string;
  public adviceId: number;
  public origin: string;
  public symbol: any = {};
  public isAutomatic: boolean = true;
  public isFractionary: boolean;
  public stock: ICheckoutStock = {};
  public finance: FinancePosition;

  public priceBandLoaded: boolean;
  public instrumentLoaded: boolean;
  public suitability: string;
  public cblc: number;
  public totalGuarantee: number;
  public consolidatedPosition: ConsolidatedPosition;
  public closingTimeDayTrade: string | Date;
  public closingTimeBovespa: string | Date;

  public dialogResumeReference: MatDialogRef<CheckoutResumeComponent>;
  public dialogAboutFeesReference: MatDialogRef<CheckoutAboutFeesComponent>;
  public dialogGuaranteeReference: MatDialogRef<CheckoutGuaranteeWarningSidePanelComponent>;

  public get isLoaded() {
    if (this.isFractionary) return this.instrumentLoaded;

    return this.priceBandLoaded && this.instrumentLoaded;
  }

  // Altera a propriedade que controla as abas de investimento (automatico/manual)
  public set checkoutType(isAutomatic: boolean) {
    this.isAutomatic = isAutomatic;
  }

  // Retorna o preço unitário do ativo
  public get getUnitPrice() {
    return (
      this.symbol.PriceLastTrade || this.symbol.OpeningPrice || this.symbol.ClosingPricePreviousDay || this.symbol.priceLastTrade || this.symbol.openingPrice || this.symbol.closingPricePreviousDay
    );
  }

  private stockCheckout: StockCheckoutType = new StockCheckoutType();

  public get isFuture(): boolean {
    return this.symbol.typeMarket === OrderMarketTypeEnum.Future;
  }

  public constructor(
    private checkoutService: CheckoutService,
    protected dialogsServices: DialogsService,
    private checkoutNotifierService: CheckoutNotifierService,
    private store: Store<AppState>,
    private settings: SettingsService,
    private tracking: TrackingsService,
  ) {
    super();
    this.subscriptionStockCheckout();

    this.disposableSubscriptions.push(
      this.store
        .select((storeAuth) => storeAuth.authentication)
        .filter((result) => !!result.currentUser)
        .subscribe((result) => {
          this.cblc = result.currentUser.cblc;
        }),


      this.store.select((store) => store.exchange.tradingTime)
        .subscribe((data) => {
          this.closingTimeBovespa = data.limitDayTradeStock;
          this.closingTimeDayTrade = data.limitDayTradeFuture;
        }),

      this.store
        .select((state) => state.finance.consolidatePosition)
        .pipe(filter((consolidatePosition) => !!consolidatePosition))
        .subscribe((consolidatePosition) => {
          this.consolidatedPosition = consolidatePosition;
        }),

      this.store.select((store) => store.finance)
        .subscribe((finance) => {
          this.finance = finance.finance;
        })
    );

    this.disposableSubscriptions.push(
      this.store
        .select((storeSuit) => storeSuit.suitability)
        .filter((result) => !!(result && result.profile))
        .subscribe((result) => {
          this.suitability = result.profile.description;
        })
    );

    this.sendCheckoutVisualizationIntention();
    this.bindSubscriptions();
  }

  public ngOnInit() {
    this.totalGuarantee = this.consolidatedPosition.totalOperationGuarantee - Math.abs(this.availableToInvest);
    this.isAutomatic = false;
  }

  public get availableToInvest() {
    return this.calculeBalanceByProjectedPosition(this.finance);
  }

  public get guaranteeValueUsed() {
    if (this.availableToInvest >= this.investments) {
      return 0;
    }

    return this.investments - this.availableToInvest;
  }

  public get investments() {
    return calculateInvestment(this.stock);
  }

  public get isUsingGuaranteeValue() {
    if (this.guaranteeValueUsed && (this.guaranteeValueUsed <= this.totalGuarantee)) {
      return true;
    }

    return false;
  }

  public bindSubscriptions() {
    this.subscriptionIntrumentPriceBand();
    this.researchSubscription();
    this.subscriptionStockMarket();
  }

  // Recebe o ID da ação e/ou ordem
  public subscriptionStockCheckout() {
    this.disposableSubscriptions.push(
      this.checkoutService.getStockItemStoreData().subscribe((data) => {
        if (data) {
          this.stockCheckout = data;
          this.stockId = this.stockCheckout.symbol.toUpperCase();
          this.isFractionary = this.stockId.endsWith("F");
          this.isAutomatic = !this.isFractionary;
          this.origin = this.stockCheckout.origin;

          this.checkoutService.requestInstrumentData(this.stockId, this.isFractionary);
          this.checkoutService.requestQuoteData(this.stockId);

          if (this.stockCheckout.adviceId) {
            this.adviceId = this.stockCheckout.adviceId;
          }

          this.tracking.sendMetricsToMixpanel(ActionEventsEnum.MODAL_OPENED, {
            'Product Area Origin': 'Bolsa',
            'Scenario': this.stockCheckout.purchase ? 'Compra de ativo' : 'Venda de ativo',
            'Step': 'Checkout',
            'Time Responsável': 'Roebling',
            'specificProperties': {
              'Ticker': this.stockId,
            }
          });
        }
      })
    );
  }

  public researchSubscription() {
    // Get Advice
    this.disposableSubscriptions.push(
      this.checkoutService.getOpportunityStoreData(this.adviceId).subscribe((opportunity) => {
        if (opportunity) {
          if (!opportunity.avaliable) {
            this.closeDialog();
          } else {
            this.stock.isDayTrade = opportunity.isDayTrade;
            this.stock.purchase = opportunity.side === OrderSideEnum.Compra;
            this.stock.category = this.stockCheckout.category;
            this.handleGainLossValues(opportunity);
          }
        }
      })
    );

    this.disposableSubscriptions.push(
      this.checkoutService.getPriceBandStoreData(this.stockId).subscribe((priceBand) => {
        this.handleGainLossValues(priceBand);
      })
    );

    this.disposableSubscriptions.push(
      this.checkoutService.getOpportunityStoreErrorData().subscribe((opportunityError) => {
        this.checkoutService.dispatchGenericError(opportunityError.payload);
        this.closeDialog();
      })
    );

    this.disposableSubscriptions.push(
      this.checkoutService.getPriceBandStoreErrorData().subscribe((priceBandError) => {
        this.checkoutService.dispatchGenericError(priceBandError.payload);
        this.closeDialog();
      })
    );
  }

  public handleGainLossValues(data) {
    this.stock.stopGainVariation = this.stock.stopLossVariation = 0;
    this.stock.unitGainPrice = data.goal;
    this.stock.unitLossPrice = data.stop;
    this.priceBandLoaded = true;
  }

  // Busca as informações de intrumento e pernas da operação
  public subscriptionIntrumentPriceBand() {
    this.disposableSubscriptions.push(
      this.checkoutService.getInstrumentStoreData(this.stockId).subscribe((instrument) => {
        this.symbol = instrument;
        this.stock = {
          stockId: this.stockId,
          unitPrice: this.getUnitPrice,
          origin: this.origin, // Id do ativo
          purchase: this.stockCheckout.purchase,
          isDayTrade: this.stockCheckout.isFuture,
          stress: this.symbol.stress,
          logo: this.symbol.logo.content, // Logo
          descriptionStock: this.symbol.descriptionStock, // Descrição do ativo
          typeMarket: this.symbol.typeMarket, // Se futuro ou Bovespa
          priceStep: this.symbol.rangeMinimumPriceIncrements, // Variação na edição de preço
          lotDefault: this.symbol.standardLot, // Lote padrão
          buyAmount: this.symbol.standardLot, // Quantidade de lotes na compra
          quoteFactor: this.symbol.quoteFactor,
          isFractionary: this.isFractionary,
          isFuture: this.symbol.typeMarket === OrderMarketTypeEnum.Future,
        };
        this.instrumentLoaded = true;

        if (this.isFractionary) {
          this.mainFractionaryInstrumentSubscription();
          return;
        }

        if (!this.adviceId) this.checkoutService.requestPriceBandData(this.stockId, this.stock.isDayTrade);
        else this.checkoutService.requestOpportunityData(this.adviceId);
      })
    );

    this.disposableSubscriptions.push(
      this.checkoutService.getInstrumentStoreErrorData().subscribe((instrumentError) => {
        this.checkoutService.dispatchGenericError(instrumentError.payload);
        this.closeDialog();
      })
    );
  }

  public mainFractionaryInstrumentSubscription() {
    this.disposableSubscriptions.push(
      this.checkoutService.getInstrumentStoreData(this.stockId.slice(0, -1)).subscribe((instrument) => {
        this.stock.mainLotDefault = instrument.standardLot;
      })
    );
  }

  // Se inscreve na cotação
  public subscriptionStockMarket() {
    this.disposableSubscriptions.push(
      this.checkoutService.getQuoteStoreData(this.stockId).subscribe((quote) => {
        this.handleSymbolQuote(quote);
      })
    );
  }

  public handleSymbolQuote(quote: any) {
    if (!this.stock) return;
    if (!this.stock.stress) this.stock.stress = {};
    // Preço Unitário = Última Cotação senão Preço de Abertura senão Preço de Fechamento
    if (quote.Stress) {
      this.stock.stress.value = quote.Stress.Value;
      this.stock.stress.dayTradeStrategyValue = quote.Stress.DayTradeStrategyValue;
      this.stock.stress.dayTradeValue = quote.Stress.DayTradeValue;
      this.stock.stress.isPtax = quote.Stress.IsPtax;
    }

    if (quote.PriceLastTrade) {
      this.stock.unitPrice = quote.PriceLastTrade || quote.OpeningPrice || quote.ClosingPricePreviousDay || quote.priceLastTrade || quote.openingPrice || quote.closingPricePreviousDay;
    }
  }

  public ngOnDestroy() {
    this.disposableSubscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  public closeDialog() {
    this.close.emit();
  }

  public investmentResume() {
    this.tracking.sendMetricsToMixpanel(ActionEventsEnum.ELEMENT_CLICKED, {
      'Product Area Origin': 'Bolsa',
      Element: 'Botão continuar para a revisão',
      Group: 'Não se aplica',
      Step: 'Checkout',
      Scenario: this.stock.purchase ? 'Compra de ativo' : 'Venda de ativo',
      Status: 'Não se aplica',
      Label: 'Não se aplica',
      Text: 'Não se aplica',
      'Time Responsável': 'Roebling',
      specificProperties: {
        Ticker: this.stock.stockId,
        'Price Mode': !!this.stock.editedPrice ? 'Personalizado' : 'A mercado'
      }
    });

    this.stock.isAutomaticCheckout = this.isAutomatic;

    if ((this.availableToInvest + this.totalGuarantee) < calculateInvestment(this.stock)) {
      this.dialogsServices.showSimpleModal(
        "icon_info",
        "Garantia insuficiente",
        `Você não tem garantia suficiente para fazer esse investimento.
              Você pode transferir mais recursos para a sua conta e tentar novamente.`,
        `Voltar`
      );
    } else {
      if (
        this.isUsingGuaranteeValue
      ) {
        const reference = this.dialogsServices.showComponentAsAsidePanel(CheckoutGuaranteeWarningSidePanelComponent, true, { stock: this.stock, guaranteeValueUsed: this.guaranteeValueUsed, });

        this.dialogGuaranteeReference = reference as MatDialogRef<CheckoutGuaranteeWarningSidePanelComponent>;

        this.dialogGuaranteeReference.afterClosed()
          .subscribe((result) => {
            if (result) {
              this.resumeInvestmentRedirect();
            }
          });
      } else {
        this.resumeInvestmentRedirect();
      }
    }
  }

  public resumeInvestmentRedirect() {
    const reference = this.dialogsServices.showComponentAsAsidePanel(CheckoutResumeComponent, true, { stock: this.stock, guaranteeValueUsed: this.guaranteeValueUsed, closingTimeBovespa: this.closingTimeBovespa, closingTimeDayTrade: this.closingTimeDayTrade });

    this.dialogResumeReference = reference as MatDialogRef<CheckoutResumeComponent>;

    this.disposableSubscriptions.push(
      this.dialogResumeReference.afterClosed()
        .subscribe(() => {
          if (!this.dialogResumeReference.componentInstance.pending) {
            this.close.emit();
          }
          this.dialogResumeReference = null;
        }),
    );
  }

  private sendCheckoutVisualizationIntention() {
    const minimumAmountToNotify = this.settings.getConfigurationKeyValue("minimumAmountToNotify");

    if (this.stockCheckout.purchase && this.consolidatedPosition.accountBalance >= minimumAmountToNotify) {
      this.checkoutNotifierService
        .sendCheckoutNotifierIntention({
          "cblc": this.cblc,
          "dateOperation": new Date().getTime(),
          "productType": CheckoutNotifierProductType.BOLSA,
          "userActionType": CheckoutNotifierActionType.Visualization,
          "items": [
            {
              "id": 0,
              "name": this.stockCheckout.symbol.toUpperCase(),
            },
          ],
        })
        .subscribe();
    }
  }

  public aboutFeesCall() {
    const reference = this.dialogsServices.showComponentAsAsidePanel(CheckoutAboutFeesComponent, true);
    this.dialogAboutFeesReference = reference as MatDialogRef<CheckoutAboutFeesComponent>;
  }

  public calculeBalanceByProjectedPosition(financePosition: FinancePosition): number {
    return financePosition.availableOnAccountD0 + this.getProjectedRisk(this.finance);
  }

  private getProjectedRisk(financePosition: FinancePosition): number {
    return financePosition.projectedD1 + financePosition.projectedD2;
  }
}
